import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { usePDIContext } from '../context/PDIContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { CHECKLIST_SECTIONS } from '../data/checklistData';
import { FEATURE_BASED_CHECKS } from '../data/modelSpecificChecks';
import ChecklistItem from '../components/ChecklistItem';
import ItemDetailModal from '../components/ItemDetailModal';
import {
  FileText,
  Car,
  Armchair,
  Zap,
  Cog,
  Circle,
  Droplet,
  FolderOpen,
  Navigation,
  Sparkles,
  AlertTriangle,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  X,
  Flag,
} from 'lucide-react';

const iconMap = {
  FileText,
  Car,
  Armchair,
  Zap,
  Cog,
  Circle,
  Droplet,
  FolderOpen,
  Navigation,
  Sparkles,
};

const ChecklistPage = () => {
  const navigate = useNavigate();
  const {
    sectionProgress,
    itemStatuses,
    carProfile,
    markSectionAsPass,
  } = usePDIContext();

  usePageMeta({
    title: 'Pre-Delivery Inspection Checklist - 100+ Point Guided Walkthrough',
    description: 'Go through a guided, section-by-section PDI checklist covering exterior, interior, engine, tyres, documents, and more. Add photos and notes as evidence.',
    path: '/checklist',
  });

  // Feature-based sections (from car profile) come after the standard ones
  const allSections = useMemo(() => {
    const features = carProfile?.features || [];
    const featureSections = features
      .filter(f => FEATURE_BASED_CHECKS[f])
      .map(f => ({
        id: `feature-${f}`,
        title: FEATURE_BASED_CHECKS[f].name,
        icon: 'Sparkles',
        description: `Additional checks for ${FEATURE_BASED_CHECKS[f].name}`,
        isFeature: true,
        items: FEATURE_BASED_CHECKS[f].items,
      }));
    return [...CHECKLIST_SECTIONS, ...featureSections];
  }, [carProfile?.features]);

  const getProgress = (section) => {
    if (!section.isFeature) return sectionProgress[section.id];
    const total = section.items.length;
    const completed = section.items.filter(
      item => itemStatuses[item.id] && itemStatuses[item.id] !== 'pending'
    ).length;
    return { completed, total, percentage: total ? Math.round((completed / total) * 100) : 0 };
  };

  // Resume at the first section that still has unchecked items
  const [activeIndex, setActiveIndex] = useState(() => {
    const idx = allSections.findIndex(section =>
      section.items.some(item => !itemStatuses[item.id] || itemStatuses[item.id] === 'pending')
    );
    return idx === -1 ? 0 : idx;
  });
  const [showOverview, setShowOverview] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showMarkAllConfirm, setShowMarkAllConfirm] = useState(false);

  const safeIndex = Math.min(activeIndex, allSections.length - 1);
  const section = allSections[safeIndex];
  const progress = getProgress(section);
  const isLastSection = safeIndex === allSections.length - 1;
  const SectionIcon = iconMap[section.icon] || FileText;

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setShowMarkAllConfirm(false);
  }, [safeIndex]);

  const goToSection = (index) => {
    setActiveIndex(index);
    setShowOverview(false);
  };

  const handleNext = () => {
    if (progress.percentage === 100) {
      track('completed_section', { section: section.title });
    }
    if (isLastSection) {
      navigate('/summary');
    } else {
      setActiveIndex(safeIndex + 1);
    }
  };

  const sectionComplete = progress.percentage === 100;

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 animate-fade-in pb-40">
      {/* Stepper */}
      <div className="sticky top-[61px] z-20 -mx-4 px-4 py-3 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200/50">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-400">
              SECTION {safeIndex + 1} OF {allSections.length}
            </p>
            <div className="flex gap-1 mt-1.5 overflow-x-auto scrollbar-hide pb-1">
              {allSections.map((s, i) => {
                const p = getProgress(s);
                const isActive = i === safeIndex;
                const isDone = p.percentage === 100;
                return (
                  <button
                    key={s.id}
                    onClick={() => goToSection(i)}
                    aria-label={s.title}
                    className={`flex-shrink-0 w-7 h-7 rounded-full text-[11px] font-bold flex items-center justify-center transition-all ${
                      isActive
                        ? 'bg-slate-900 text-white scale-110 shadow-md'
                        : isDone
                          ? 'bg-brand-500 text-white'
                          : s.isFeature
                            ? 'bg-violet-100 text-violet-700 border border-violet-300'
                            : p.completed > 0
                              ? 'bg-accent-100 text-accent-700 border border-accent-300'
                              : 'bg-white text-slate-400 border border-slate-200'
                    }`}
                  >
                    {isDone && !isActive ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : s.isFeature && !isActive ? (
                      <Sparkles className="w-3.5 h-3.5" />
                    ) : (
                      i + 1
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            onClick={() => setShowOverview(true)}
            className="flex-shrink-0 w-10 h-10 rounded-xl bg-white border border-slate-200 text-slate-500 flex items-center justify-center hover:border-brand-300 hover:text-brand-600 transition-colors"
            aria-label="All sections"
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Current Section */}
      <div className="mt-4">
        <div
          className={`rounded-2xl border-2 shadow-sm overflow-hidden ${
            section.isFeature
              ? 'bg-gradient-to-br from-violet-50/50 to-purple-50/50 border-violet-200'
              : sectionComplete
                ? 'bg-gradient-to-br from-brand-50/30 to-emerald-50/30 border-brand-200'
                : 'bg-white border-slate-200'
          }`}
        >
          {/* Section header */}
          <div className="px-4 py-4 flex items-center gap-4">
            <div className={`relative w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${
              section.isFeature
                ? 'bg-gradient-to-br from-violet-400 to-purple-500'
                : sectionComplete
                  ? 'bg-gradient-to-br from-brand-500 to-emerald-500'
                  : 'bg-gradient-to-br from-slate-400 to-slate-500'
            }`}>
              <SectionIcon className="w-6 h-6 text-white" />
              {sectionComplete && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
                  <Check className="w-3.5 h-3.5 text-brand-500" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-slate-800 text-lg truncate">{section.title}</h2>
                {section.critical && (
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-accent-100 text-accent-600 rounded text-[10px] font-bold flex-shrink-0">
                    <AlertTriangle className="w-3 h-3" />
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r transition-all duration-500 ${
                      section.isFeature
                        ? 'from-violet-400 to-purple-500'
                        : sectionComplete
                          ? 'from-brand-400 to-emerald-500'
                          : 'from-accent-400 to-orange-500'
                    }`}
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                <span className={`text-sm font-bold ${sectionComplete ? 'text-brand-600' : 'text-slate-500'}`}>
                  {progress.completed}/{progress.total}
                </span>
              </div>
            </div>
          </div>

          <div className="px-4 pb-4">
            {section.description && (
              <p className="text-sm text-slate-500 mb-4 bg-slate-50/80 p-3 rounded-xl border border-slate-100">
                💡 {section.description}
              </p>
            )}

            {/* Mark All as Pass */}
            {progress.completed < progress.total && (
              <div className="mb-3">
                {!showMarkAllConfirm ? (
                  <button
                    onClick={() => setShowMarkAllConfirm(true)}
                    className="w-full py-2.5 px-4 rounded-xl border-2 border-dashed border-brand-200 text-brand-600 hover:bg-brand-50 hover:border-brand-300 transition-all flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <CheckCheck className="w-4 h-4" />
                    Mark All as Pass
                  </button>
                ) : (
                  <div className="p-3 rounded-xl border-2 bg-brand-50 border-brand-200">
                    <p className="text-sm text-slate-700 mb-3 text-center">
                      Mark all {progress.total} items in this section as passed?
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowMarkAllConfirm(false)}
                        className="flex-1 py-2 px-3 rounded-lg bg-white border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          markSectionAsPass(section.id, section.items);
                          setShowMarkAllConfirm(false);
                        }}
                        className="flex-1 py-2 px-3 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                      >
                        <CheckCheck className="w-4 h-4" />
                        Confirm
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Items */}
            <div className="space-y-2">
              {section.items.map((item, itemIndex) => (
                <ChecklistItem
                  key={item.id}
                  item={item}
                  onExpand={() => setSelectedItem(item)}
                  itemIndex={itemIndex}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Prev / Next footer */}
      <div className="fixed bottom-16 left-0 right-0 z-20 px-4 pb-3 pt-2 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent">
        <div className="max-w-3xl mx-auto flex gap-3">
          <button
            onClick={() => setActiveIndex(safeIndex - 1)}
            disabled={safeIndex === 0}
            className="w-14 h-14 rounded-2xl bg-white border-2 border-slate-200 text-slate-500 flex items-center justify-center btn-touch disabled:opacity-40 hover:border-slate-300 transition-colors shadow-sm"
            aria-label="Previous section"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={handleNext}
            className={`flex-1 h-14 rounded-2xl font-bold text-white flex items-center justify-center gap-2 btn-touch shadow-lg transition-all ${
              isLastSection
                ? 'bg-gradient-to-r from-slate-800 to-slate-900'
                : sectionComplete
                  ? 'bg-gradient-to-r from-brand-500 to-emerald-500'
                  : 'gradient-animated'
            }`}
          >
            {isLastSection ? (
              <>
                <Flag className="w-5 h-5" />
                Finish - View Report
              </>
            ) : (
              <>
                Next: {allSections[safeIndex + 1].title}
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Section Overview */}
      {showOverview && (
        <div className="modal-backdrop flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in max-h-[80vh] flex flex-col">
            <div className="px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white flex items-center justify-between flex-shrink-0">
              <h2 className="font-display font-semibold text-lg">All Sections</h2>
              <button onClick={() => setShowOverview(false)} className="p-2 -m-2 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto divide-y divide-slate-100">
              {allSections.map((s, i) => {
                const p = getProgress(s);
                const Icon = iconMap[s.icon] || FileText;
                const isDone = p.percentage === 100;
                return (
                  <button
                    key={s.id}
                    onClick={() => goToSection(i)}
                    className={`w-full p-4 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors ${
                      i === safeIndex ? 'bg-brand-50/60' : ''
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isDone
                        ? 'bg-gradient-to-br from-brand-500 to-emerald-500'
                        : s.isFeature
                          ? 'bg-gradient-to-br from-violet-400 to-purple-500'
                          : 'bg-slate-100'
                    }`}>
                      {isDone ? (
                        <Check className="w-5 h-5 text-white" />
                      ) : (
                        <Icon className={`w-5 h-5 ${s.isFeature ? 'text-white' : 'text-slate-500'}`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-700 truncate text-sm">{s.title}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${isDone ? 'bg-brand-500' : 'bg-accent-400'}`}
                            style={{ width: `${p.percentage}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-slate-400">{p.completed}/{p.total}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};

export default ChecklistPage;
